import { formatPercentage } from '@if/core/utils'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledPercentField,
  ControlledPercentSlider,
  Help,
  StatCard,
  useFormContainerContext,
  useDebouncedWatch,
} from '@if/mui'
import { Alert, Box, Divider, Skeleton, Stack, Typography } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CustomBuydownFormValues, MERCHANT_FEE_RATE_MIN, MERCHANT_FEE_RATE_MAX } from '../customBuydown-schema'
import { useGetCustomBuydownTermRates } from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { applyRebateToTermRates } from '@src/data/customBuydownRates'

// Skeletons match a StatCard's size so the dialog height stays stable while rates load.
const TERM_CARD_HEIGHT = 110
const TERM_SKELETON_COUNT = 5

const contributionMarks = [0, 5, 10, 15, 20, 25, 30].map((value) => ({ value, label: `${value}%` }))

export interface PlanFormFieldsProps {
  merchantId: string
  adminFeeRate: number
  rebate: number
  disabled?: boolean
  bannerPlanValues?: CustomBuydownFormValues | null
}

export const PlanFormFields = ({ merchantId, adminFeeRate, rebate, disabled = false, bannerPlanValues = null }: PlanFormFieldsProps) => {
  const { t } = useTranslation()
  const { methods } = useFormContainerContext<CustomBuydownFormValues>()
  const debouncedContribution = useDebouncedWatch({ control: methods.control, name: 'contribution' })
  const debouncedMaxAmount = useDebouncedWatch({ control: methods.control, name: 'maxFinancingAmount' })
  const contribution = useWatch({ control: methods.control, name: 'contribution' }) ?? 0
  const activeFrom = useWatch({ control: methods.control, name: 'activeFrom' })
  const expiresOn = useWatch({ control: methods.control, name: 'expiresOn' })
  const maxFinancingAmount = useWatch({ control: methods.control, name: 'maxFinancingAmount' })

  const { isValid } = methods.formState
  const canPreview =
    isValid &&
    debouncedContribution >= MERCHANT_FEE_RATE_MIN &&
    debouncedContribution <= MERCHANT_FEE_RATE_MAX &&
    (debouncedMaxAmount === null || debouncedMaxAmount >= 0)

  const [termRates, isLoading] = useGetCustomBuydownTermRates(
    merchantId, 
    debouncedContribution, 
    adminFeeRate, 
    debouncedMaxAmount, 
    canPreview
  )

  const previewRates = termRates && applyRebateToTermRates(termRates, rebate)
  const showSkeletons = !canPreview || (isLoading && !termRates)

  const matchesBannerPlan =
    !!bannerPlanValues &&
    Math.abs(contribution - bannerPlanValues.contribution) <= 1e-6 &&
    (activeFrom?.toString() ?? '') === bannerPlanValues.activeFrom.toString() &&
    (expiresOn?.toString() ?? '') === (bannerPlanValues.expiresOn?.toString() ?? '') &&
    (maxFinancingAmount ?? null) === (bannerPlanValues.maxFinancingAmount ?? null)

  return (
    <Stack spacing={3} divider={<Divider />}>
      {!disabled && matchesBannerPlan && (
        <Alert severity="info">{t('customBuydown.inherited.fromBanner')}</Alert>
      )}
      <Stack spacing={1}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <ControlledDateOnlyPicker
            control={methods.control}
            name="activeFrom"
            label={t('customBuydown.activeFrom')}
            disabled={disabled}
            fullWidth
          />
          <ControlledDateOnlyPicker
            control={methods.control}
            name="expiresOn"
            label={t('customBuydown.expiresOn')}
            clearable
            disabled={disabled}
            fullWidth
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('customBuydown.datesHelper')}
        </Typography>
      </Stack>
      <ControlledNumberField
        control={methods.control}
        name="maxFinancingAmount"
        label={t('customBuydown.maxFinancingAmount')}
        helperText={t('customBuydown.maxHelper')}
        isMoney
        disableDecimals
        disabled={disabled}
        fullWidth
      />

      <Stack spacing={1}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="subtitle2">{t('customBuydown.contribution')}</Typography>
          <Help title={t('customBuydown.contributionTooltip')} />
        </Stack>
        <ControlledPercentField
          control={methods.control}
          name="contribution"
          label={t('customBuydown.contributionLabel')}
          decimals={2}
          required
          disabled={disabled}
          fullWidth
        />
        <Box sx={{ px: 1 }}>
          <ControlledPercentSlider
            control={methods.control}
            name="contribution"
            min={MERCHANT_FEE_RATE_MIN * 100}
            max={MERCHANT_FEE_RATE_MAX * 100}
            step={0.01}
            marks={contributionMarks}
            disabled={disabled}
          />
        </Box>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle2">{t('customBuydown.patientRates')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5 }}>
          {showSkeletons 
            ? Array.from({ length: TERM_SKELETON_COUNT }).map((_, index) => (
                <Skeleton key={index} variant="rounded" height={TERM_CARD_HEIGHT} />
              )) : (previewRates ?? []).map((termRate) => (
            <StatCard
              key={termRate.termMonths}
              variant="outlined"
              label={t('customBuydown.termLabel', { months: termRate.termMonths })}
              value={`${formatPercentage(termRate.promoRate * 100)}%`}
              caption={t('customBuydown.termRateFullTerm')}
            />
          ))}
        </Box>
      </Stack>
    </Stack>
  )
}
